import React, {
    useEffect,
    useMemo,
    useState,
} from "react";

import {
    Sparkles,
    Loader2,
    ChevronLeft
} from "lucide-react";

import {
    useDispatch,
    useSelector,
} from "react-redux";

import {
    useNavigate,
} from "react-router-dom";

import type {
    RootState,
    AppDispatch,
} from "../../store";

import MobileFrame from "../../components/MobileFrame";

import {
    updateScheme,
} from "../../api/journey.api";

import {
    setJourney,
} from "../../store/journeySlice";

import {
    setSchemes,
    setSelectedScheme,
} from "../../store/schemeSlice";

import {
    updateJourneyPhase,
} from "../../api/journey.api";

import {
    fetchApplicableSchemes,
} from "../../api/scheme.api";

import { ApiError } from "../../api/ApiError";



const SchemePage: React.FC = () => {

    const navigate =
        useNavigate();

    const dispatch =
        useDispatch<AppDispatch>();


    /*
     * =====================================================
     * JOURNEY
     * =====================================================
     */

    const journey =
        useSelector(
            (state: RootState) =>
                state.journey.journey
        );


    /*
     * =====================================================
     * SCHEMES FROM REDUX
     * =====================================================
     */

    const schemes =
        useSelector(
            (state: RootState) =>
                state.scheme.schemes
        );


    /*
     * =====================================================
     * STATE
     * =====================================================
     */

    const [
        selectedSchemeId,
        setSelectedSchemeId,
    ] = useState<string>("");


    const [
        isSubmitting,
        setIsSubmitting,
    ] = useState(false);


    const [
        submitError,
        setSubmitError,
    ] = useState<string | null>(null);

    const [
        isGoingBack,
        setIsGoingBack,
    ] = useState(false);

    const [isLoadingSchemes, setIsLoadingSchemes] =
        useState(false);

    const [schemeLoadError, setSchemeLoadError] =
        useState<string | null>(null);

    useEffect(() => {

        if (!journey) {
            return;
        }

        // Schemes already available in Redux.
        if (schemes.length > 0) {
            return;
        }

        const amount = Number(journey.orderAmount);

        if (
            !Number.isFinite(amount) ||
            amount <= 0
        ) {
            setSchemeLoadError(
                "Unable to determine the loan amount."
            );
            return;
        }

        const lender = journey.lender;

        if (!lender) {
            setSchemeLoadError(
                "Unable to determine the lender."
            );
            return;
        }

        let cancelled = false;

        const loadSchemes = async () => {

            try {

                setIsLoadingSchemes(true);
                setSchemeLoadError(null);

                const response =
                    await fetchApplicableSchemes({
                        lender,
                        amount,
                    });

                if (cancelled) {
                    return;
                }

                const availableSchemes =
                    response?.data?.availableSchemes;

                if (
                    !Array.isArray(availableSchemes) ||
                    availableSchemes.length === 0
                ) {
                    setSchemeLoadError(
                        "No repayment schemes are available for this application."
                    );
                    return;
                }

                dispatch(
                    setSchemes({
                        amount:
                            response.data.amount ?? amount,
                        schemes:
                            availableSchemes,
                    })
                );

            } catch (error) {

                if (cancelled) {
                    return;
                }

                let message =
                    "Unable to load repayment schemes. Please try again.";

                if (error instanceof ApiError) {

                    if (
                        error.status >= 500 &&
                        error.status < 600
                    ) {
                        message =
                            "Something went wrong. Please try again later.";
                    } else {
                        message =
                            error.message?.trim() ||
                            message;
                    }
                }

                setSchemeLoadError(message);

            } finally {

                if (!cancelled) {
                    setIsLoadingSchemes(false);
                }

            }
        };

        loadSchemes();

        return () => {
            cancelled = true;
        };

    }, [
        journey,
        schemes.length,
        dispatch,
    ]);


    /*
     * =====================================================
     * SELECTED SCHEME
     * =====================================================
     */

    const selectedScheme =
        useMemo(() => {

            if (!selectedSchemeId) {
                return schemes[0] ?? null;
            }

            return (
                schemes.find(
                    calculatedScheme =>
                        calculatedScheme.scheme._id ===
                        selectedSchemeId
                ) ?? null
            );

        }, [
            schemes,
            selectedSchemeId,
        ]);


    /*
     * =====================================================
     * DEFAULT FIRST SCHEME
     * =====================================================
     *
     * If schemes are loaded from Redux and no scheme has
     * been selected yet, the first scheme is selected.
     */

    React.useEffect(() => {

        if (
            schemes.length > 0 &&
            !selectedSchemeId
        ) {

            const firstScheme =
                schemes[0]?.scheme;

            if (firstScheme?._id) {

                setSelectedSchemeId(
                    firstScheme._id
                );

            }

        }

    }, [
        schemes,
        selectedSchemeId,
    ]);


    /*
     * =====================================================
     * ORDER / LOAN AMOUNT
     * =====================================================
     */

    const totalAmount =
        Number(
            journey?.orderAmount
        );


    const hasValidAmount =
        Number.isFinite(totalAmount) &&
        totalAmount > 0;


    /*
     * =====================================================
     * PAYMENT BREAKDOWN
     * =====================================================
     *
     * Use the calculations returned by scheme/fetch.
     */

    const downPayment =
        selectedScheme?.calculations
            ?.totalDownPayment ?? 0;


    const netLoanAmount =
        selectedScheme?.calculations
            ?.netLoanAmount ?? 0;


    /*
     * =====================================================
     * CONTINUE
     * =====================================================
     */

    const handleContinue =
        async () => {

            /*
             * Prevent duplicate submission.
             */

            if (isSubmitting) {
                return;
            }


            /*
             * Validate journey.
             */

            if (!journey) {

                setSubmitError(
                    "Your application session is unavailable. Please try again."
                );

                return;
            }


            /*
             * Validate amount.
             */

            if (!hasValidAmount) {

                setSubmitError(
                    "Unable to determine the loan amount. Please try again."
                );

                return;
            }


            /*
             * Validate selected scheme.
             */

            if (
                !selectedScheme ||
                !selectedScheme.scheme._id
            ) {

                setSubmitError(
                    "Please select a repayment scheme."
                );

                return;
            }


            try {

                setSubmitError(null);

                setIsSubmitting(true);


                /*
                 * Send ONLY the selected scheme ID.
                 */

                const response =
                    await updateScheme({
                        schemeId:
                            selectedScheme.scheme._id,
                    });


                /*
                 * Validate API response.
                 */

                if (
                    !response?.data
                ) {

                    throw new Error(
                        "Invalid response received while updating the scheme."
                    );

                }


                /*
                 * Update journey in Redux.
                 */

                dispatch(
                    setJourney(
                        response.data
                    )
                );


                /*
                 * Keep ONLY the selected scheme
                 * in scheme Redux.
                 */

                dispatch(
                    setSelectedScheme(
                        selectedScheme
                    )
                );


                /*
                 * Navigate after successful
                 * scheme update.
                 */

                navigate(
                    "/ndashboard",
                    {
                        replace: true,
                    }
                );


            } catch (error) {

                setIsSubmitting(false);

                let message =
                    "Unable to continue. Please try again.";

                if (error instanceof ApiError) {

                    if (
                        error.status >= 500 &&
                        error.status < 600
                    ) {
                        message =
                            "Something went wrong. Please try again later.";
                    } else {
                        message =
                            error.message?.trim() ||
                            message;
                    }
                }

                setSubmitError(message);
            }

        };

    /*
* =====================================================
* BACK — RETURN TO PRE-APPROVAL
* =====================================================
*
* Mirrors handleContinue: the forward transition updates
* the journey phase on the backend, so going back must
* revert it symmetrically. Otherwise a refresh or re-entry
* after navigating back would still resolve to
* SCHEME_SELECTION and bounce the user forward again.
*/
    const handleBack =
        async () => {

            if (
                isSubmitting ||
                isGoingBack
            ) {
                return;
            }

            if (!journey) {

                navigate(
                    "/napproval",
                    { replace: true }
                );

                return;
            }

            try {

                setIsGoingBack(true);

                setSubmitError(null);

                const response =
                    await updateJourneyPhase(
                        "PRE_APPROVAL"
                    );

                if (response?.data) {

                    dispatch(
                        setJourney(
                            response.data
                        )
                    );

                }

                navigate(
                    "/napproval",
                    { replace: true }
                );

            } catch (error) {

                let message =
                    "Unable to go back right now. Please try again.";

                if (error instanceof ApiError) {

                    if (
                        error.status >= 500 &&
                        error.status < 600
                    ) {
                        message =
                            "Something went wrong. Please try again later.";
                    } else {
                        message =
                            error.message?.trim() ||
                            message;
                    }
                }

                setSubmitError(message);
            } finally {

                setIsGoingBack(false);

            }

        };


    /*
     * =====================================================
     * FORMAT AMOUNT
     * =====================================================
     */

    const formatAmount =
        (
            amount: number
        ) => {

            return `₹${amount.toLocaleString(
                "en-IN"
            )}`;

        };


    /*
 * =====================================================
 * INVALID JOURNEY / AMOUNT
 * =====================================================
 */

    if (!journey || !hasValidAmount) {

        return (
            <MobileFrame
                glowColor="#c7d2fe"
                borderColor="#e5e7eb"
                width={420}
            >
                <div className="flex h-full items-center justify-center bg-white px-6">

                    <div className="text-center">

                        <p className="text-sm font-medium text-gray-700">
                            Unable to load your application.
                        </p>

                        <p className="mt-1 text-xs text-gray-400">
                            Please reopen your application and try again.
                        </p>

                    </div>

                </div>
            </MobileFrame>
        );
    }


    /*
     * =====================================================
     * LOADING SCHEMES
     * =====================================================
     */

    if (isLoadingSchemes) {

        return (
            <MobileFrame
                glowColor="#c7d2fe"
                borderColor="#e5e7eb"
                width={420}
            >
                <div className="flex h-full flex-col items-center justify-center bg-white px-6">

                    <Loader2
                        className="h-8 w-8 animate-spin text-indigo-500"
                        aria-hidden="true"
                    />

                    <p className="mt-4 text-sm font-semibold text-gray-700">
                        Loading repayment schemes...
                    </p>

                    <p className="mt-1 text-xs text-gray-400">
                        Please wait while we prepare your available plans.
                    </p>

                </div>
            </MobileFrame>
        );
    }


    /*
     * =====================================================
     * SCHEME LOAD ERROR
     * =====================================================
     */

    if (
        schemeLoadError ||
        schemes.length === 0
    ) {

        return (
            <MobileFrame
                glowColor="#c7d2fe"
                borderColor="#e5e7eb"
                width={420}
            >
                <div className="flex h-full items-center justify-center bg-white px-6">

                    <div className="w-full text-center">

                        <div className="mx-auto flex h-10 w-10 items-center justify-center rounded-full bg-red-50">
                            <span className="text-lg font-bold text-red-500">
                                !
                            </span>
                        </div>

                        <p className="mt-3 text-sm font-semibold text-gray-700">
                            Unable to load repayment schemes
                        </p>

                        <p className="mt-1 text-xs text-gray-400">
                            {schemeLoadError ||
                                "Please try again."}
                        </p>

                        <button
                            type="button"
                            onClick={() => {
                                window.location.reload();
                            }}
                            className="mt-5 rounded-full bg-gradient-to-r from-indigo-500 to-blue-400 px-6 py-2.5 text-xs font-semibold text-white"
                        >
                            Try Again
                        </button>

                    </div>

                </div>
            </MobileFrame>
        );
    }


    /*
     * =====================================================
     * UI
     * =====================================================
     */

    return (

        <MobileFrame
            glowColor="#c7d2fe"
            borderColor="#e5e7eb"
            width={420}
        >

            <div
                className="
                    relative
                    w-full
                    h-full
                    overflow-hidden
                    bg-gradient-to-br
                    from-white
                    via-gray-50
                    to-gray-100
                "
            >

                {/* Decorative background */}

                <div
                    aria-hidden="true"
                    className="
                        absolute
                        w-64
                        h-64
                        bg-purple-200
                        rounded-full
                        -top-24
                        -left-20
                        blur-3xl
                        opacity-20
                    "
                />

                <div
                    aria-hidden="true"
                    className="
                        absolute
                        w-64
                        h-64
                        bg-blue-200
                        rounded-full
                        -bottom-24
                        -right-20
                        blur-3xl
                        opacity-20
                    "
                />


                <div
                    className="
                        relative
                        z-10
                        flex
                        flex-col
                        h-full
                        px-6
                        pt-5
                        pb-3
                        overflow-hidden
                    "
                >

                    {/* HEADER */}

                    <div className="flex items-center justify-between shrink-0">

                        <div className="flex items-center gap-3">

                            <button
                                type="button"
                                onClick={handleBack}
                                disabled={
                                    isSubmitting ||
                                    isGoingBack
                                }
                                aria-label="Go back to pre-approval"
                                className="
                flex h-9 w-9
                items-center justify-center
                rounded-full
                border border-slate-200
                bg-white
                text-gray-600
                shadow-sm
                active:scale-95
                transition-transform
                disabled:cursor-not-allowed
                disabled:opacity-50
            "
                            >

                                {isGoingBack ? (
                                    <Loader2
                                        className="h-4.5 w-4.5 animate-spin"
                                        aria-hidden="true"
                                    />
                                ) : (
                                    <ChevronLeft
                                        className="h-4.5 w-4.5"
                                        aria-hidden="true"
                                    />
                                )}

                            </button>

                            <p className="text-base font-bold text-gray-900">
                                Choose EMI Plan
                            </p>

                        </div>

                        <img
                            src="/CleverPeLogos/CleverPE_Black_icon.svg"
                            alt="CleverPe"
                            className="h-6 w-6 opacity-90 -mt-3"
                        />

                    </div>


                    <p className="mt-1.5 text-[12.5px] text-gray-500 leading-snug shrink-0">
                        Select a repayment plan that works
                        best for you.
                    </p>


                    {/* PAYMENT BREAKDOWN */}

                    <div
                        className="
                            mt-3
                            w-full
                            shrink-0
                            rounded-xl
                            border
                            border-gray-200
                            bg-white
                            px-4
                            py-2.5
                        "
                    >

                        <p
                            className="
                                mb-2
                                text-[12px]
                                font-semibold
                                text-gray-700
                            "
                        >
                            Payment breakdown
                        </p>


                        <div className="grid grid-cols-3 items-center gap-2">

                            {/* TOTAL */}

                            <div>

                                <p className="text-[10px] text-gray-500">
                                    Total Amount
                                </p>

                                <p
                                    className="
                                        mt-0.5
                                        text-[12.5px]
                                        font-semibold
                                        text-gray-900
                                    "
                                >
                                    {formatAmount(
                                        totalAmount
                                    )}
                                </p>

                            </div>


                            {/* DOWN PAYMENT */}

                            <div
                                className="
                                    border-l
                                    border-gray-100
                                    pl-3
                                "
                            >

                                <p className="text-[10px] text-gray-500">
                                    Down Payment
                                </p>

                                <p
                                    className="
                                        mt-0.5
                                        text-[12.5px]
                                        font-semibold
                                        text-gray-900
                                    "
                                >
                                    {formatAmount(
                                        downPayment
                                    )}
                                </p>

                            </div>


                            {/* NET LOAN */}

                            <div
                                className="
                                    border-l
                                    border-gray-100
                                    pl-3
                                "
                            >

                                <p className="text-[10px] text-gray-500">
                                    Net Loan
                                </p>

                                <p
                                    className="
                                        mt-0.5
                                        text-[12.5px]
                                        font-semibold
                                        text-gray-900
                                    "
                                >
                                    {formatAmount(
                                        netLoanAmount
                                    )}
                                </p>

                            </div>

                        </div>

                    </div>


                    {/* SELECT SCHEME */}

                    <div className="mt-3.5 flex-1 min-h-0 flex flex-col">

                        <p
                            className="
                                mb-1.5
                                text-[12px]
                                font-semibold
                                text-gray-700
                                shrink-0
                            "
                        >
                            Select Scheme
                        </p>


                        <div className="flex-1 min-h-0 flex flex-col gap-3">

                            {schemes.map(
                                (
                                    calculatedScheme,
                                    index
                                ) => {

                                    const scheme =
                                        calculatedScheme.scheme;

                                    const calculations =
                                        calculatedScheme.calculations;

                                    const schemeId =
                                        scheme._id ??
                                        scheme.schemeId ??
                                        `scheme-${index}`;

                                    const isSelected =
                                        selectedSchemeId ===
                                        schemeId;

                                    return (

                                        <button
                                            key={schemeId}
                                            type="button"
                                            disabled={
                                                isSubmitting
                                            }
                                            onClick={() =>
                                                setSelectedSchemeId(
                                                    schemeId
                                                )
                                            }
                                            aria-pressed={
                                                isSelected
                                            }
                                            className={`
                        relative
                        w-full
                        text-left
                        rounded-xl
                        border
                        bg-white
                        px-3.5
                        py-2
                        transition-all
                        disabled:cursor-not-allowed
                        disabled:opacity-60
                        ${isSelected
                                                    ? "border-emerald-500 ring-1 ring-emerald-100"
                                                    : "border-gray-200"
                                                }
                    `}
                                        >

                                            {/* RECOMMENDED */}

                                            {index === 0 && (
                                                <span
                                                    className="
            absolute
            -top-2.5
            right-3
            z-10
            flex
            items-center
            gap-0.5
            whitespace-nowrap
            rounded-full
            border
            border-indigo-200
            bg-indigo-50
            px-2
            py-0.5
            text-[9px]
            font-semibold
            text-indigo-600
            shadow-sm
        "
                                                >
                                                    <Sparkles
                                                        aria-hidden="true"
                                                        className="h-2.5 w-2.5"
                                                    />
                                                    Recommended
                                                </span>
                                            )}

                                            <div
                                                className="
                            flex
                            items-center
                            justify-between
                        "
                                            >

                                                {/* MONTHLY EMI × TENURE */}

                                                <div className="flex items-center gap-2">
                                                    {/* <span
                                                        className="
            inline-flex
            items-center
            rounded-md
            bg-blue-50
            px-2
            py-1
            text-[10px]
            font-bold
            text-blue-700
        "
                                                    >
                                                        {calculations.name}
                                                    </span> */}

                                                    <p
                                                        className="
            text-[12px]
            font-bold
            text-gray-600
        "
                                                    >
                                                        {formatAmount(calculations.monthlyEMI)}
                                                        {" × "}
                                                        {calculations.NoOfNetEmis} months
                                                    </p>
                                                </div>


                                                {/* ANNUAL INTEREST */}

                                                <div className="text-right">

                                                    <p
                                                        className="
                                    text-[11px]
                                    font-medium
                                    text-gray-600
                                "
                                                    >
                                                        {
                                                            scheme.annualInterest
                                                        }%

                                                        <span
                                                            className="
                                        ml-1
                                        text-[9px]
                                        font-medium
                                        text-gray-600
                                    "
                                                        >
                                                            annually
                                                        </span>

                                                    </p>

                                                </div>

                                            </div>

                                        </button>

                                    );

                                }
                            )}

                        </div>

                    </div>


                    {/* ERROR */}

                    {submitError && (

                        <p
                            role="alert"
                            className="
                                mt-2
                                text-center
                                text-[11px]
                                font-medium
                                text-red-500
                                shrink-0
                            "
                        >
                            {submitError}
                        </p>

                    )}


                    {/* CONTINUE */}

                    <button
                        type="button"
                        onClick={
                            handleContinue
                        }
                        disabled={
                            isSubmitting ||
                            !selectedScheme
                        }
                        className={`
                            w-full
                            py-2.5
                            mt-3
                            mb-3
                            text-sm
                            font-semibold
                            rounded-full
                            flex
                            items-center
                            justify-center
                            gap-2
                            transition-all
                            text-white
                            shrink-0
                            ${isSubmitting ||
                                !selectedScheme
                                ? "bg-gradient-to-r from-indigo-300 to-blue-200 cursor-not-allowed"
                                : "bg-gradient-to-r from-indigo-500 to-blue-400 hover:opacity-95"
                            }
                        `}
                    >

                        {isSubmitting && (
                            <Loader2
                                aria-hidden="true"
                                className="h-4 w-4 animate-spin"
                            />
                        )}

                        {isSubmitting
                            ? "Please wait..."
                            : "Continue"}

                    </button>


                    {/* FOOTER */}

                    <p
                        className="
                            mb-1
                            text-center
                            text-[11px]
                            font-semibold
                            tracking-wide
                            text-gray-400
                            shrink-0
                        "
                    >
                        CleverPe
                    </p>

                </div>

            </div>

        </MobileFrame>

    );

};


export default SchemePage;